import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import { useEventImageUrl } from '../lib/image-sas';
import { hasFreshSwrCache, swrLoad } from '../lib/swrCache';
import type { CalendarPost, ExtractedEvent } from '../types';
import EventFiltersBar from '../components/EventFiltersBar';
import GroupBySelector from '../components/GroupBySelector';
import SectionTabs from '../components/SectionTabs';
import { EVENTS_TABS } from '../lib/section-tabs';
import {
  type EventGroupNode,
  type GroupByKey,
  groupEvents,
  groupingFromParam,
  groupingToParam,
} from '../lib/eventGrouping';
import {
  type FilterState,
  applyFilters,
  DATE_WINDOW_ERROR,
  presetWindow,
  readFiltersFromUrl,
  writeFiltersToUrl,
} from '../lib/eventFilters';
import { compareEventDates, eventDayKey, formatRange, isAllDayEvent } from '../lib/eventDates';

/**
 * Adapter: present a CalendarPost as an ExtractedEvent so the existing
 * `EventFiltersBar`, `applyFilters`, and `groupEvents` work without forking.
 *
 * Critical mappings:
 *   - `startUtc` \u2190 `postedAt ?? fetchedAt`  (drives the date preset filter)
 *   - `description` \u2190 `rawText`  (powers free-text search)
 *   - `imageUrl` \u2190 `rawImageBlobUrl`  (powers the "Image" toggle and SAS resolution)
 *   - `tags`/`category` left empty so unused tag/category facets stay quiet
 */
function postToSyntheticEvent(p: CalendarPost): ExtractedEvent {
  // Use the LATEST extracted-event date as the synthetic startUtc so this
  // page's sort + grouping reflect when the post's events actually happen,
  // not when the post was ingested. Falls back to postedAt/fetchedAt for
  // posts with no extracted events yet.
  const latestEvent = (p.events ?? [])
    .filter((event) => typeof event.startUtc === 'string' && eventDayKey(event) !== null)
    .sort(compareEventDates)
    .at(-1);
  return {
    partitionKey: p.partitionKey,
    rowKey: p.rowKey,
    title: p.calendarSummary || p.calendarPeriodLabel || `Calendar post (${p.sourceType})`,
    description: p.rawText,
    startUtc: latestEvent?.startUtc ?? p.postedAt ?? p.fetchedAt,
    isAllDay: latestEvent ? isAllDayEvent(latestEvent) : false,
    locationText: p.venueName,
    venueId: p.venueId,
    venueName: p.venueName,
    sourceId: p.sourceId,
    sourceType: p.sourceType,
    rawId: p.rowKey,
    confidence: 1,
    status: 'published',
    imageUrl: p.rawImageBlobUrl,
    createdAt: p.fetchedAt,
    updatedAt: p.fetchedAt,
    neighborhood: p.neighborhood,
    rawUrl: p.rawUrl,
    venueExcludedTags: p.venueExcludedTags,
    tags: [],
  };
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return new Date(iso).toLocaleDateString();
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return new Date(iso).toLocaleDateString();
}

function cssSafe(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 64);
}

export default function CalendarPostsPage() {
  const [params, setParams] = useSearchParams();
  // The filter bar is shared with /events; we read/write the same URL state
  // so users can copy the URL for a filtered view as before.
  const [filters, setFiltersState] = useState<FilterState>(() => readFiltersFromUrl(params));
  const [groupBy, setGroupByState] = useState<GroupByKey[]>(
    () => {
      const raw = groupingFromParam(params.get('group'));
      // Default to grouping by venue for first-time visitors. Honor an
      // explicit group="" (no grouping) by leaving the array empty.
      return params.has('group') ? raw : (['venue'] as GroupByKey[]);
    }
  );
  const [posts, setPosts] = useState<CalendarPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const extraParams = (): Record<string, string | undefined> => ({
    group: groupingToParam(groupBy),
  });

  const setFilters = (updater: (f: FilterState) => FilterState) => {
    setFiltersState((prev) => {
      const next = updater(prev);
      writeFiltersToUrl(next, extraParams(), (p) => setParams(p, { replace: true }));
      return next;
    });
  };

  const setGroupBy = (next: GroupByKey[]) => {
    setGroupByState(next);
    writeFiltersToUrl(
      filters,
      { ...extraParams(), group: groupingToParam(next) },
      (p) => setParams(p, { replace: true })
    );
  };

  useEffect(() => {
    const window = presetWindow(filters);
    if (!window) {
      setError(DATE_WINDOW_ERROR);
      setPosts([]);
      return;
    }
    setError(null);
    const { from, to } = window;
    const fromIso = from.toISOString();
    const toIso = to.toISOString();
    const qs = `from=${fromIso}&to=${toIso}`;
    const key = `calendar-posts:${fromIso}|${toIso}`;
    if (!hasFreshSwrCache(key)) setPosts(null);
    let cancelled = false;
    void swrLoad<CalendarPost[]>({
      key,
      fetcher: () => api.get<CalendarPost[]>(`/api/events/calendar-posts?${qs}`),
      onData: (data) => setPosts(data),
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, [filters.preset, filters.customFrom, filters.customTo]);

  const synthetic = useMemo(() => (posts ?? []).map(postToSyntheticEvent), [posts]);
  const filtered = useMemo(() => applyFilters(synthetic, filters), [synthetic, filters]);

  const postById = useMemo(() => {
    const m = new Map<string, CalendarPost>();
    for (const p of posts ?? []) m.set(p.rowKey, p);
    return m;
  }, [posts]);

  const groups = useMemo(() => groupEvents(filtered, groupBy), [filtered, groupBy]);

  return (
    <>
      <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
        <h1 className="h4 mb-0">Calendar posts</h1>
      </SectionTabs>

      <p className="text-body-secondary small mb-3">
        Original calendar / schedule / multi-event flyer posts from venue feeds. One card per source post,
        grouped by venue by default. The filter bar below is the same one used on the Events page.
      </p>

      <EventFiltersBar events={synthetic} filters={filters} setFilters={setFilters} />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <GroupBySelector value={groupBy} onChange={setGroupBy} />
        <span className="text-body-secondary small">
          {filtered.length === (posts?.length ?? 0)
            ? `${filtered.length} post${filtered.length === 1 ? '' : 's'}`
            : `${filtered.length} of ${posts?.length ?? 0} posts`}
        </span>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {posts === null ? (
        <LoadingBlock label="Loading calendar posts\u2026" />
      ) : posts.length === 0 ? (
        <div className="alert alert-info" role="status">
          No calendar posts ingested in this date window yet. Calendar-post detection runs on every new
          ingest \u2014 expand the date range or wait for the next venue scan.
        </div>
      ) : filtered.length === 0 ? (
        <div className="alert alert-warning" role="status">
          No calendar posts match the current filters. Try clearing the filter bar.
        </div>
      ) : (
        <CalendarPostGroupTree groups={groups} postById={postById} />
      )}
    </>
  );
}

interface GroupTreeProps {
  groups: EventGroupNode[];
  postById: Map<string, CalendarPost>;
}

function CalendarPostGroupTree({ groups, postById }: GroupTreeProps) {
  return (
    <div className="d-flex flex-column gap-4">
      {groups.map((g) => (
        <GroupNode key={`${g.key}|${g.value}`} node={g} postById={postById} depth={0} />
      ))}
    </div>
  );
}

function GroupNode({ node, postById, depth }: { node: EventGroupNode; postById: Map<string, CalendarPost>; depth: number }) {
  const headingLevel = Math.min(2 + depth, 6) as 2 | 3 | 4 | 5 | 6;
  const HeadingTag: 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = `h${headingLevel}`;
  const showHeader = node.value !== '__all__' || node.label !== 'All events';
  return (
    <section
      className={`mh-calendar-post-group mh-calendar-post-group--depth-${depth} mh-calendar-post-group--${cssSafe(node.value)}`}
      aria-label={node.label}
    >
      {showHeader && (
        <HeadingTag className={`h${Math.min(4 + depth, 6)} mb-3 d-flex align-items-baseline gap-2`}>
          <span>{node.label}</span>
          <span className="badge text-bg-secondary fw-normal">{node.count}</span>
        </HeadingTag>
      )}
      {node.children.length > 0 ? (
        <div className="d-flex flex-column gap-4 ms-md-3">
          {node.children.map((child) => (
            <GroupNode key={`${child.key}|${child.value}`} node={child} postById={postById} depth={depth + 1} />
          ))}
        </div>
      ) : (
        <div className="row g-3">
          {node.events.map((e) => {
            const post = postById.get(e.rowKey);
            if (!post) return null;
            return (
              <div key={post.id} className="col-12 col-lg-6">
                <CalendarPostCard post={post} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CalendarPostCard({ post }: { post: CalendarPost }) {
  const resolveImage = useEventImageUrl();
  // Use the post's own mirrored images first; if none (older ingests, websites
  // that didn't capture an image), fall back to images the extractor pulled
  // onto child events. The backend already merges these, but we also dedupe
  // here defensively in case future shapes change.
  const seenImages = new Set<string>();
  const allImages: string[] = [];
  const pushImg = (u?: string) => {
    if (!u || seenImages.has(u)) return;
    seenImages.add(u);
    allImages.push(u);
  };
  if (post.rawImageBlobUrls && post.rawImageBlobUrls.length > 0) {
    for (const u of post.rawImageBlobUrls) pushImg(u);
  } else {
    pushImg(post.rawImageBlobUrl);
  }
  for (const e of post.events ?? []) pushImg(e.imageUrl);

  const [activeIdx, setActiveIdx] = useState(0);
  const activeRaw = allImages[activeIdx];
  const activeUrl = resolveImage(activeRaw);
  const events = post.events ?? [];

  return (
    <article className="card h-100 shadow-sm">
      {activeUrl ? (
        <a
          href={activeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="d-block bg-body-tertiary"
          aria-label="Open full-size image in a new tab"
        >
          <img
            src={activeUrl}
            alt={post.calendarSummary
              ? `Calendar post: ${post.calendarSummary}`
              : `Calendar post from ${post.venueName ?? post.sourceType}`}
            className="card-img-top w-100"
            style={{ maxHeight: 600, objectFit: 'contain', backgroundColor: 'var(--bs-tertiary-bg)' }}
            loading="lazy"
          />
        </a>
      ) : allImages.length > 0 ? (
        <div className="card-img-top bg-body-tertiary d-flex align-items-center justify-content-center" style={{ height: 240 }}>
          <span className="text-body-secondary small">Loading image\u2026</span>
        </div>
      ) : (
        // Match the height of the image area when there IS an image so cards
        // line up in the grid (text content starts at the same Y).
        <div className="card-img-top bg-body-tertiary d-flex align-items-center justify-content-center" style={{ height: 240 }}>
          <span className="text-body-secondary small">No image (text-only post)</span>
        </div>
      )}

      {allImages.length > 1 && (
        <div
          className="d-flex gap-1 px-2 py-2 bg-body-tertiary border-top overflow-auto"
          role="tablist"
          aria-label="Carousel images"
        >
          {allImages.map((raw, idx) => {
            const thumb = resolveImage(raw);
            const selected = idx === activeIdx;
            return (
              <button
                key={raw}
                type="button"
                role="tab"
                aria-selected={selected ? 'true' : 'false'}
                aria-label={`Image ${idx + 1} of ${allImages.length}`}
                className={`btn p-0 border ${selected ? 'border-primary border-2' : 'border-transparent'}`}
                style={{ flex: '0 0 auto', height: 56, width: 56, overflow: 'hidden' }}
                onClick={() => setActiveIdx(idx)}
              >
                {thumb ? (
                  <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span className="visually-hidden">Loading</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          {post.calendarKind && (
            <span className="badge text-bg-info text-uppercase small">{post.calendarKind.replace(/-/g, ' ')}</span>
          )}
          <span className="badge text-bg-secondary text-capitalize">{post.sourceType}</span>
          {post.calendarPeriodLabel && (
            <span className="badge text-bg-light border">{post.calendarPeriodLabel}</span>
          )}
          <span className="text-body-secondary small ms-auto" title={post.postedAt ?? post.fetchedAt}>
            {relativeTime(post.postedAt ?? post.fetchedAt)}
          </span>
        </div>

        {post.venueId && post.venueName ? (
          <a href={`/venues/${encodeURIComponent(post.venueId)}`} className="card-title h6 mb-1 d-block text-decoration-none">
            {post.venueName}
          </a>
        ) : (
          <p className="card-title h6 mb-1 text-body-secondary">{post.venueName ?? '(Unknown venue)'}</p>
        )}

        {post.calendarSummary && (
          <p className="card-text mb-2">{post.calendarSummary}</p>
        )}

        {post.rawText && (
          <details className="mb-2">
            <summary className="small text-body-secondary">Show original caption</summary>
            <p className="small mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>{post.rawText}</p>
          </details>
        )}

        {events.length > 0 && (
          <details className="mb-2">
            <summary className="small text-body-secondary">
              {events.length} event{events.length === 1 ? '' : 's'} extracted from this post
            </summary>
            <ul className="list-unstyled small mb-0 mt-2">
              {events.map((ev) => {
                const label = (
                  <>
                    <span className="text-body-secondary" style={{ minWidth: '8rem' }}>
                      {/* The API sends the derived month label, not the source quote; it is a month-only quote. */}
                      {formatRange(ev.monthOnlyLabel ? { ...ev, sourceDateText: ev.monthOnlyLabel } : ev)}
                    </span>
                    <span className="flex-grow-1">{ev.title}</span>
                    {ev.venueName && (
                      <span className="text-body-secondary">@ {ev.venueName}</span>
                    )}
                  </>
                );
                return (
                  <li key={ev.id} className="py-1 border-top">
                    {post.rawUrl ? (
                      <a
                        href={post.rawUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex flex-wrap gap-2 align-items-baseline text-decoration-none"
                        title="Open original post"
                      >
                        {label}
                      </a>
                    ) : (
                      <div className="d-flex flex-wrap gap-2 align-items-baseline">
                        {label}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </details>
        )}

        <div className="d-flex flex-wrap gap-2 small">
          {post.rawUrl && (
            <a href={post.rawUrl} target="_blank" rel="noopener noreferrer" className="link-secondary">
              Open original post
            </a>
          )}
          {events.length === 0 && (
            <span className="text-body-secondary ms-auto">No events extracted yet</span>
          )}
        </div>
      </div>
    </article>
  );
}
